// 음성 캡처 (§4): getUserMedia 한 스트림으로 ① AnalyserNode로 음량/음높이/멈춤 지표 ② MediaRecorder로
// 오디오 녹음을 '병행'한다. 마이크 소비자가 getUserMedia 하나뿐이라(네이티브 STT 플러그인 미사용)
// 동시-마이크 충돌·크래시가 없다. 녹음 blob은 sttClient가 서버로 보내 전사한다.
// 실패(권한·미지원)하면 graceful: start가 false, stop이 빈 결과.

export interface VoiceCapture {
  peakVolume: number; // 0~1 (세션 최고 음량)
  pitchVariance: number; // 0~1 (음높이 변동폭)
  volumeTimeline: number[]; // 0~1 구간별 음량(곡선 재료)
  pauseRatio: number; // 0~1 (저음량=멈춤 비율) — '억눌림'(방화벽형) 축
  pauseCount: number; // 멈춤 구간 수
  durationMs: number; // 캡처 지속(발화속도 계산용)
}

export interface VoiceResult {
  metrics: VoiceCapture;
  blob: Blob; // 녹음 오디오(서버 전사용)
  mime: string;
}

// 튜닝 상수(출발점).
const SAMPLE_MS = 100;
const VOL_REF = 0.22; // RMS 정규화 기준
const SILENCE = 0.12; // 미만이면 '멈춤'
const MIN_VOICED_RMS = 0.015; // 음높이 추정 최소 RMS

let stream: MediaStream | null = null;
let ctx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let recordMime = "";

let timeline: number[] = [];
let peak = 0;
let pitches: number[] = [];
let startedAt = 0;
let liveLevels: number[] = []; // 최근 음량 샘플(라이브 비주얼용 링버퍼)
const LIVE_MAX = 64;

/** 녹음 중 라이브 비주얼용 최근 음량 샘플(0~1). 캡처 중이 아니면 빈 배열. */
export function getLiveLevels(): number[] {
  return liveLevels;
}

function cleanup(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  try {
    stream?.getTracks().forEach((t) => t.stop());
  } catch {
    /* 무시 */
  }
  try {
    void ctx?.close();
  } catch {
    /* 무시 */
  }
  stream = null;
  ctx = null;
  analyser = null;
  recorder = null;
}

function pickMime(): string | undefined {
  const MR = window.MediaRecorder as (typeof MediaRecorder & { isTypeSupported?: (t: string) => boolean }) | undefined;
  if (!MR?.isTypeSupported) return undefined;
  for (const m of ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]) {
    if (MR.isTypeSupported(m)) return m;
  }
  return undefined;
}

/** 시간영역 버퍼에서 기본 주파수(Hz) 추정. 자기상관. 유성음 아니면 -1. */
function autoCorrelate(b: Float32Array, sampleRate: number): number {
  const SIZE = b.length;
  let rms = 0;
  for (let i = 0; i < SIZE; i++) rms += b[i] * b[i];
  rms = Math.sqrt(rms / SIZE);
  if (rms < MIN_VOICED_RMS) return -1;
  let r1 = 0;
  let r2 = SIZE - 1;
  const thres = 0.2;
  for (let i = 0; i < SIZE / 2; i++)
    if (Math.abs(b[i]) < thres) {
      r1 = i;
      break;
    }
  for (let i = 1; i < SIZE / 2; i++)
    if (Math.abs(b[SIZE - i]) < thres) {
      r2 = SIZE - i;
      break;
    }
  const seg = b.subarray(r1, r2);
  const n = seg.length;
  if (n < 64) return -1;
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    for (let j = 0; j < n - i; j++) sum += seg[j] * seg[j + i];
    c[i] = sum;
  }
  let d = 0;
  while (d < n - 1 && c[d] > c[d + 1]) d++;
  let maxval = -1;
  let maxpos = -1;
  for (let i = d; i < n; i++)
    if (c[i] > maxval) {
      maxval = c[i];
      maxpos = i;
    }
  if (maxpos <= 0) return -1;
  const freq = sampleRate / maxpos;
  if (freq < 70 || freq > 500) return -1;
  return freq;
}

function sample(): void {
  if (!analyser || !ctx) return;
  const data = new Float32Array(analyser.fftSize);
  analyser.getFloatTimeDomainData(data);
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
  const rms = Math.sqrt(sum / data.length);
  const vol = Math.min(1, rms / VOL_REF);
  timeline.push(vol);
  liveLevels.push(vol);
  if (liveLevels.length > LIVE_MAX) liveLevels.shift();
  if (vol > peak) peak = vol;
  const f = autoCorrelate(data, ctx.sampleRate);
  if (f > 0) pitches.push(f);
  timer = setTimeout(sample, SAMPLE_MS);
}

export function isCapturing(): boolean {
  return analyser !== null || recorder !== null;
}

/** 마이크 캡처+녹음 시작. 성공하면 true. 실패면 false(음성입력 비활성). */
export async function startVoiceCapture(): Promise<boolean> {
  try {
    if (!navigator.mediaDevices?.getUserMedia) return false;
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // ① 지표용 AnalyserNode
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor) {
      ctx = new Ctor();
      if (ctx.state === "suspended") await ctx.resume();
      const src = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
    }

    // ② 녹음용 MediaRecorder(같은 스트림)
    chunks = [];
    if (typeof MediaRecorder !== "undefined") {
      const mime = pickMime();
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recordMime = recorder.mimeType || mime || "audio/mp4";
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorder.start();
    }

    timeline = [];
    pitches = [];
    liveLevels = [];
    peak = 0;
    startedAt = performance.now();
    if (analyser) sample();
    return analyser !== null || recorder !== null;
  } catch {
    cleanup();
    return false;
  }
}

function computeMetrics(): VoiceCapture {
  const durationMs = performance.now() - startedAt;
  const tl = timeline.slice();
  let silent = 0;
  let pauseCount = 0;
  let inPause = false;
  for (const v of tl) {
    if (v < SILENCE) {
      silent++;
      if (!inPause) {
        pauseCount++;
        inPause = true;
      }
    } else {
      inPause = false;
    }
  }
  const pauseRatio = tl.length > 0 ? silent / tl.length : 0;
  let pitchVariance = 0;
  if (pitches.length >= 3) {
    const mean = pitches.reduce((a, b) => a + b, 0) / pitches.length;
    const varc = pitches.reduce((a, p) => a + (p - mean) * (p - mean), 0) / pitches.length;
    const cv = mean > 0 ? Math.sqrt(varc) / mean : 0;
    pitchVariance = Math.max(0, Math.min(1, cv / 0.35));
  }
  return {
    peakVolume: Math.max(0, Math.min(1, peak)),
    pitchVariance,
    volumeTimeline: tl,
    pauseRatio: Math.max(0, Math.min(1, pauseRatio)),
    pauseCount,
    durationMs,
  };
}

function stopRecorder(): Promise<Blob> {
  return new Promise((resolve) => {
    const rec = recorder;
    const mime = recordMime || "audio/mp4";
    if (!rec || rec.state === "inactive") {
      resolve(new Blob(chunks, { type: mime }));
      return;
    }
    rec.onstop = () => resolve(new Blob(chunks, { type: mime }));
    try {
      rec.stop();
    } catch {
      resolve(new Blob(chunks, { type: mime }));
    }
  });
}

/** 캡처 종료 → 음량 지표 + 녹음 blob 반환. 캡처 안 했으면 null. */
export async function stopVoiceCapture(): Promise<VoiceResult | null> {
  if (!analyser && !recorder) {
    cleanup();
    return null;
  }
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const metrics = computeMetrics();
  const blob = await stopRecorder();
  const mime = blob.type || recordMime || "audio/mp4";
  cleanup();
  return { metrics, blob, mime };
}

/** 즉시 해제(언마운트 등). blob 안 받음 — 마이크만 놓는다. */
export function releaseVoiceCapture(): void {
  cleanup();
}
